import { useEffect, useState } from 'react'
import { supabase } from '../supabaseClient'
import { useAuth } from '../context/AuthContext'
import { useNavigate } from 'react-router-dom'
import Navbar from '../components/Navbar'

const MONTH_LABELS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
const DAY_LABELS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat']

export default function PatientHistory() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const [assignments, setAssignments] = useState([])
  const [interventionAssignments, setInterventionAssignments] = useState([])
  const [completions, setCompletions] = useState([])
  const [interventionCompletions, setInterventionCompletions] = useState([])
  const [selectedDate, setSelectedDate] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => { fetchData() }, [])

  async function fetchData() {
    const thirtyDaysAgo = new Date()
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 29)
    const from = thirtyDaysAgo.toISOString().split('T')[0]

    const [{ data: assignmentData }, { data: interventionData }, { data: completionData }, { data: interventionCompletionData }] = await Promise.all([
      supabase.from('assignments').select('*, exercises(*)').eq('patient_id', profile.id).order('sort_order'),
      supabase.from('intervention_assignments').select('*, interventions(*)').eq('patient_id', profile.id).order('sort_order'),
      supabase.from('completions').select('*').eq('patient_id', profile.id).gte('completed_on', from),
      supabase.from('intervention_completions').select('*').eq('patient_id', profile.id).gte('completed_on', from)
    ])

    setAssignments(assignmentData || [])
    setInterventionAssignments(interventionData || [])
    setCompletions(completionData || [])
    setInterventionCompletions(interventionCompletionData || [])
    setLoading(false)
  }

  function getLast30Days() {
    return Array.from({ length: 30 }, (_, i) => {
      const d = new Date()
      d.setDate(d.getDate() - (29 - i))
      return d.toISOString().split('T')[0]
    })
  }

  function getRate(date) {
    const total = assignments.length + interventionAssignments.length
    if (total === 0) return 0
    const done = completions.filter(c => c.completed_on === date).length +
      interventionCompletions.filter(c => c.completed_on === date).length
    return done / total
  }

  function getCompletedForDate(date) {
    const doneEx = completions.filter(c => c.completed_on === date)
    const doneIv = interventionCompletions.filter(c => c.completed_on === date)
    return {
      exercises: assignments.filter(a => doneEx.some(c => c.assignment_id === a.id)),
      tasks: interventionAssignments.filter(a => doneIv.some(c => c.intervention_assignment_id === a.id)),
      missedExercises: assignments.filter(a => !doneEx.some(c => c.assignment_id === a.id)),
      missedTasks: interventionAssignments.filter(a => !doneIv.some(c => c.intervention_assignment_id === a.id))
    }
  }

  const days = getLast30Days()
  const today = new Date().toISOString().split('T')[0]
  const totalCompleted = completions.length + interventionCompletions.length
  const totalPossible = (assignments.length + interventionAssignments.length) * 30
  const overallRate = totalPossible > 0 ? Math.round((totalCompleted / totalPossible) * 100) : 0

  if (loading) return <><Navbar /><div className="page"><p>Loading...</p></div></>

  const selectedData = selectedDate ? getCompletedForDate(selectedDate) : null

  return (
    <>
      <Navbar />
      <div className="page">
        <span onClick={() => navigate('/patient')} className="back-link" style={{ cursor: 'pointer' }}>← Back</span>
        <div className="page-header">
          <h1>My history</h1>
          <p>Last 30 days</p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: '1.5rem' }}>
          <div className="stat-card" style={{ background: 'linear-gradient(135deg, #6366f1, #4f46e5)', border: 'none' }}>
            <p className="stat-label" style={{ color: 'rgba(255,255,255,0.7)' }}>30-day rate</p>
            <p className="stat-value" style={{ color: 'white' }}>{overallRate}<span style={{ fontSize: 18, opacity: 0.6 }}>%</span></p>
          </div>
          <div className="stat-card">
            <p className="stat-label">Completed</p>
            <p className="stat-value">{totalCompleted}<span style={{ fontSize: 16, color: 'var(--text-tertiary)' }}> sessions</span></p>
          </div>
        </div>

        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: 15, marginBottom: '1rem' }}>Tap a day to see details</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(10, 1fr)', gap: 6 }}>
            {days.map(day => {
              const rate = getRate(day)
              const isSelected = day === selectedDate
              const isToday = day === today
              return (
                <div
                  key={day}
                  onClick={() => setSelectedDate(selectedDate === day ? null : day)}
                  title={day}
                  style={{
                    aspectRatio: '1', borderRadius: 6, cursor: 'pointer',
                    background: rate === 0 ? 'var(--border)' : rate < 0.5 ? '#86efac' : rate < 1 ? '#22c55e' : '#16a34a',
                    outline: isSelected ? '2px solid #111827' : isToday ? '2px solid var(--brand)' : 'none',
                    outlineOffset: 2,
                    transform: isSelected ? 'scale(1.15)' : 'scale(1)',
                    transition: 'transform 0.15s'
                  }}
                />
              )
            })}
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 12, alignItems: 'center' }}>
            <p style={{ fontSize: 11, color: 'var(--text-tertiary)', margin: 0 }}>Less</p>
            {['var(--border)', '#86efac', '#22c55e', '#16a34a'].map(c => (
              <div key={c} style={{ width: 11, height: 11, borderRadius: 3, background: c }} />
            ))}
            <p style={{ fontSize: 11, color: 'var(--text-tertiary)', margin: 0 }}>More</p>
          </div>
        </div>

        {selectedDate && selectedData && (
          <div className="card">
            <h2 style={{ fontSize: 15, marginBottom: '1rem' }}>
              {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </h2>

            {selectedData.exercises.length === 0 && selectedData.tasks.length === 0 && selectedData.missedExercises.length === 0 && selectedData.missedTasks.length === 0 && (
              <p style={{ fontSize: 14, color: 'var(--text-tertiary)' }}>Nothing scheduled this day.</p>
            )}

            {selectedData.exercises.length > 0 && (
              <>
                <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 8px' }}>Completed</p>
                {selectedData.exercises.map(a => (
                  <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ width: 20, height: 20, borderRadius: 5, background: 'var(--success)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <span style={{ color: 'white', fontSize: 11, fontWeight: 700 }}>✓</span>
                    </div>
                    <p style={{ margin: 0, fontSize: 14, color: 'var(--success)', fontWeight: 500 }}>{a.exercises.title}</p>
                  </div>
                ))}
                {selectedData.tasks.map(a => (
                  <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ width: 20, height: 20, borderRadius: 5, background: 'var(--success)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <span style={{ color: 'white', fontSize: 11, fontWeight: 700 }}>✓</span>
                    </div>
                    <p style={{ margin: 0, fontSize: 14, color: 'var(--success)', fontWeight: 500 }}>{a.interventions.title}</p>
                  </div>
                ))}
              </>
            )}

            {(selectedData.missedExercises.length > 0 || selectedData.missedTasks.length > 0) && (
              <>
                <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.5, margin: '12px 0 8px' }}>Missed</p>
                {selectedData.missedExercises.map(a => (
                  <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ width: 20, height: 20, borderRadius: 5, background: 'var(--bg)', border: '1.5px solid var(--border-strong)', flexShrink: 0 }} />
                    <p style={{ margin: 0, fontSize: 14, color: 'var(--text-tertiary)' }}>{a.exercises.title}</p>
                  </div>
                ))}
                {selectedData.missedTasks.map(a => (
                  <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ width: 20, height: 20, borderRadius: 5, background: 'var(--bg)', border: '1.5px solid var(--border-strong)', flexShrink: 0 }} />
                    <p style={{ margin: 0, fontSize: 14, color: 'var(--text-tertiary)' }}>{a.interventions.title}</p>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>
    </>
  )
}
